import { createContext, useReducer } from "react";
import { format, set } from 'date-fns';
import { API } from "../../../../API";
import { ShareAPI } from "../../../../API";
import { narrativeActorsUrl } from "../../../../utils/narrativeActors";
import { useTranslation } from 'react-i18next';
import breakdownsReducer, { initialState } from "./BreakdownsReducer";
import { showRequestError } from "../../../../utils/requestError";

export const BreakdownsContext = createContext(initialState);

export const BreakdownsProvider = ({isShare, narrative, children}) => {
  const [state, dispatch] = useReducer(breakdownsReducer, initialState);
  const { t } = useTranslation();

  const api = isShare ? ShareAPI : API;
  const openModal = () => {
    dispatch({type: 'OPEN_MODAL'})
  }

  const closeModal = () => {
    dispatch({type: 'CLOSE_MODAL'})
  }

  const setPlatformsFilter = (options) => {
    dispatch({type: 'SET_PLATFORMS_FILTER', platformsFilter: options})
  }

  const setCountriesFilter = (options) => {
    dispatch({type: 'SET_COUNTRIES_FILTER', countriesFilter: options})
  }

  const setCountriesExcludeFilter = (bool) => {
    dispatch({type: 'SET_COUNTRIES_EXCLUDE_FILTER', countriesExcludeFilter: bool})
  }

  const setTypesFilter = (options) => {
    dispatch({type: 'SET_TYPES_FILTER', typesFilter: options})
  }

  const setSubtypesFilter = (options) => {
    dispatch({type: 'SET_SUBTYPES_FILTER', subtypesFilter: options})
  }

  const setAffiliationCountryId = (countryId) => {
    dispatch({type: 'SET_AFFILIATION_COUNTRY_ID', countryId: countryId})
  }

  const setAffiliationCountriesExcludeFilter = (bool) => {
    dispatch({type: 'SET_AFFILIATION_COUNTRIES_EXCLUDE_FILTER', affiliationExcludeFilter: bool})
  }

  const setAffiliationTypesFilter = (options) => {
    dispatch({type: 'SET_AFFILIATION_TYPES_FILTER', affiliationTypesFilter: options})
  }

  const setAffiliationTypesExcludeFilter = (bool) => {
    dispatch({type: 'SET_AFFILIATION_TYPES_EXCLUDE_FILTER', affiliationTypesExcludeFilter: bool})
  }

  const setHighRiskExcludeFilter = (bool) => {
    dispatch({type: 'SET_HIGH_RISK_EXCLUDE_FILTER', highRiskExcludeFilter: bool})
  }

  const setBotsExcludeFilter = (bool) => {
    dispatch({type: 'SET_BOTS_EXCLUDE_FILTER', botsExcludeFilter: bool})
  }

  const setAudienceFilter = (range) => {
    dispatch({type: 'SET_AUDIENCE_FILTER', audienceRange: range})
  }

  const setSourceGroupFilter = (id) => {
    dispatch({type: 'SET_SOURCE_GROUP_FILTER', sourceGroupId: [id]})
  }

  const setTagsFilter = (tags) => {
    dispatch({type: 'SET_TAGS_FILTER', tagsFilter: tags})
  }

  const setSourceFilter = (id) => {
    dispatch({type: 'SET_SOURCE_FILTER', sourceId: [id]})
  }

  const setCurrentWidgetName = (widgetName) => {
    dispatch({type: 'SET_CURRENT_WIDGET_NAME', widgetName: widgetName})
  }

  const setModalTitle = (title) => {
    dispatch({type: 'SET_MODAL_TITLE', modalTitle: title})
  }

  const fetchStateAffiliated = (narrativeId) => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `state_affiliated_breakdown`);
    const url = narrativeActorsUrl(narrativeId, 'state-affiliated', urlParams);
    api.fetch('GET', url).then(
      data => {
        dispatch({type: 'SET_STATE_AFFILIATED', stateAffiliated: data})
      }
    ).catch(e => showRequestError(e, t('State-affiliated actors')))
  }

  const fetchHighRisk = (narrativeId) => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `high_risk_accounts_breakdown`);
    const url = narrativeActorsUrl(narrativeId, 'high-risk', urlParams);
    api.fetch('GET', url).then(
      data => {
        dispatch({type: 'SET_HIGH_RISK', highRisk: data})
      }
    ).catch(e => showRequestError(e, t('High-risk actors')))
  }

  const fetchInauthenticBehavior = (narrativeId) => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `inauthentic_behavior_breakdown`);
    const url = narrativeActorsUrl(narrativeId, 'inauthentic', urlParams);
    api.fetch('GET', url).then(
      data => {
        dispatch({type: 'SET_INAUTHENTIC_BEHAVIOR', inauthenticBehavior: data})
      }
    ).catch(e => showRequestError(e, t('Inauthentic behavior')))
  }


  const value = {
    isModalOpen: state.isModalOpen,
    openModal: () => openModal(),
    closeModal: () => closeModal(),
    platformsFilter: state.platformsFilter,
    sourcesSubtypesFilter: state.sourcesSubtypesFilter,
    countriesFilter: state.countriesFilter,
    countriesExcludeFilter: state.countriesExcludeFilter,
    sourcesTypesFilter: state.sourcesTypesFilter,
    affiliationCountryId: state.affiliationCountryId,
    affiliationCountriesExcludeFilter: state.affiliationCountriesExcludeFilter,
    affiliationTypesFilter: state.affiliationTypesFilter,
    affiliationTypesExcludeFilter: state.affiliationTypesExcludeFilter,
    highRiskExcludeFilter: state.highRiskExcludeFilter,
    botsExcludeFilter: state.botsExcludeFilter,
    audienceFilter: state.audienceFilter,
    sourceGroupFilter: state.sourceGroupFilter,
    tagsFilter: state.tagsFilter,
    sourceFilter: state.sourceFilter,
    widgetName: state.widgetName,
    modalTitle: state.modalTitle,
    stateAffiliated: state.stateAffiliated,
    highRisk: state.highRisk,
    inauthenticBehavior: state.inauthenticBehavior,
    setPlatformsFilter: (options) => setPlatformsFilter(options), 
    setCountriesFilter: (options) => setCountriesFilter(options),
    setCountriesExcludeFilter: (bool) => setCountriesExcludeFilter(bool),
    setTypesFilter: (options) => setTypesFilter(options),
    setSubtypesFilter: (options) => setSubtypesFilter(options),
    setAffiliationCountryId: (countryId) => setAffiliationCountryId(countryId),
    setAffiliationCountriesExcludeFilter: (bool) => setAffiliationCountriesExcludeFilter(bool),
    setAffiliationTypesFilter: (options) => setAffiliationTypesFilter(options),
    setAffiliationTypesExcludeFilter: (bool) => setAffiliationTypesExcludeFilter(bool),
    setHighRiskExcludeFilter: (bool) => setHighRiskExcludeFilter(bool),
    setBotsExcludeFilter: (bool) => setBotsExcludeFilter(bool),
    setAudienceFilter: (range) => setAudienceFilter(range),
    setSourceGroupFilter: (id) => setSourceGroupFilter(id),
    setTagsFilter: (tags) => setTagsFilter(tags),
    setSourceFilter: (id) => setSourceFilter(id),
    setCurrentWidgetName: (widgetName) => setCurrentWidgetName(widgetName),
    setModalTitle: (title) => setModalTitle(title),
    fetchStateAffiliated: (narrativeId) => fetchStateAffiliated(narrativeId),
    fetchHighRisk: (narrativeId) => fetchHighRisk(narrativeId),
    fetchInauthenticBehavior: (narrativeId) => fetchInauthenticBehavior(narrativeId),
  }

  return (
    <BreakdownsContext.Provider value={value}>
      {children}
    </BreakdownsContext.Provider>
  )
}
